/* global pendo */
import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  Avatar,
  InlineTextButton,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ListingLink,
  OwnListingLink,
} from '../../components';
import { TopbarSearchForm } from '../../forms';

import image from '../../assets/Polis_Logo.png';

import css from './TopbarDesktop.css';

const TopbarDesktop = props => {
  const {
    className,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    isEducator,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
  } = props;
  const [mounted, setMounted] = useState(false);



  const initializePendo = () => {

    // console.log("-------------------------------")
    // console.log(" Initializing Pendo In Desktop")
    // console.log(currentUser.attributes.email)
    // console.log(currentUser.id.uuid)
    // console.log(currentUser.attributes.profile.lastName)
    // console.log("-------------------------------")
    pendo.initialize({
      visitor: {
        id:           currentUser.id.uuid,  // Required if user is logged in, default creates anonymous ID
        email:        currentUser.attributes.email, // Recommended if using Pendo Feedback, or NPS Email
        full_name:    currentUser.attributes.profile.firstName + ' ' + currentUser.attributes.profile.lastName// Recommended if using Pendo Feedback
        // role:         // Optional

        // You can add any additional visitor level key-values here,
        // as long as it's not one of the above reserved names.
      },

      account: {
        id:           currentUser.id.uuid // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
        // name:         // Optional
        // is_paying:    // Recommended if using Pendo Feedback
        // monthly_value:// Recommended if using Pendo Feedback
        // planLevel:    // Optional
        // planPrice:    // Optional
        // creationDate: // Optional

        // You can add any additional account level key-values here,
        // as long as it's not one of the above reserved names.
      }
    });
  }

  if(currentUser != null) {
    initializePendo();
  }

  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings || isEducator ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const sideProfileEducatorMenu = (
    <MenuContent className={css.profileMenuContent}>
      <MenuItem key="EditListingPage">
        <OwnListingLink
          listing={currentUserListing}
          listingFetched={currentUserListingFetched}
          className={css.yourListingsLink}
        >
          <div>
            <span className={css.menuItemBorder} />
            {currentUserListing ? (
              <FormattedMessage id="TopbarDesktop.editYourListingLink" />
            ) : (
              <FormattedMessage id="TopbarDesktop.addYourListingLink" />
            )}
          </div>
        </OwnListingLink>
      </MenuItem>
      <MenuItem key="ProfileSettingsPage">
        <NamedLink
          className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
        </NamedLink>
      </MenuItem>
      <MenuItem key="AccountSettingsPage">
        <NamedLink
          className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
        </NamedLink>
      </MenuItem>
      <MenuItem key="logout">
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.logout" />
        </InlineTextButton>
      </MenuItem>
    </MenuContent>
  );
  const sideProfileStudentMenu = (
    <MenuContent className={css.profileMenuContent}>
      <MenuItem key="ProfileSettingsPage">
        <NamedLink
          className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
        </NamedLink>
      </MenuItem>
      <MenuItem key="AccountSettingsPage">
        <NamedLink
          className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
        </NamedLink>
      </MenuItem>
      <MenuItem key="logout">
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarDesktop.logout" />
        </InlineTextButton>
      </MenuItem>
    </MenuContent>
  );

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      {isEducator ? sideProfileEducatorMenu : sideProfileStudentMenu}
    </Menu>
  ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const listingLink =
    authenticatedOnClientSide && currentUserListingFetched && currentUserListing ? (
      <ListingLink
        className={css.createListingLink}
        listing={currentUserListing}
        children={
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.viewListing" />
          </span>
        }
      />
    ) : null;

  const createListingLink =
    isAuthenticatedOrJustHydrated && !(currentUserListingFetched && !currentUserListing) ? null : (
      <NamedLink className={css.createListingLink} name="NewListingPage">
        <span className={css.createListing}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    );


  return (
    <nav className={classes}>
      <NamedLink className={css.logoLink} name="LandingPage">
        <img
          className={css.logo}
          src={image}
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
        />
      </NamedLink>
      {search}
      {isEducator && listingLink}
      {isEducator && createListingLink}
      {inboxLink}
      {profileMenu}
      {/*{signupLink}*/}
      {loginLink}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  currentUserListing: null,
  currentUserListingFetched: false,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
};

export default TopbarDesktop;
